import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function TermsOfServicePage() {
  const { currentTheme } = useTheme();

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.background }}>
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          {/* Header */}
          <div className="mb-12 text-center">
            <h1 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: currentTheme.foreground, fontFamily: 'Playfair Display, serif' }}
            >
              Terms of Service
            </h1>
            <p style={{ color: currentTheme.foreground, opacity: 0.7 }}>
              Last updated: March 2026
            </p>
          </div>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none"
            style={{ color: currentTheme.foreground }}
          >
            {/* Acceptance of Terms */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Acceptance of Terms
              </h2>
              <p className="mb-4 leading-relaxed">
                By downloading, installing, or using applications developed by Nspire Living Healthy ("we," "us," or "our"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our applications.
              </p>
            </section>

            {/* License Grant */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                License Grant
              </h2>
              <p className="mb-4 leading-relaxed">
                We grant you a limited, non-exclusive, non-transferable, revocable license to use our applications for personal, non-commercial purposes. This license does not include the right to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Modify, reverse engineer, or decompile the application</li>
                <li>Copy, distribute, or create derivative works</li>
                <li>Remove any proprietary notices or labels</li>
                <li>Use the application for any commercial purpose</li>
                <li>Circumvent, disable, or interfere with advertising features</li>
              </ul>
            </section>

            {/* Advertisements */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Advertisements
              </h2>
              <p className="mb-4 leading-relaxed">
                Our applications display advertisements provided by third-party advertising networks. By using our applications, you agree to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>View advertisements as part of the application experience</li>
                <li>The collection of data by advertising networks for ad personalization</li>
                <li>Not interfere with, block, or circumvent advertisement displays</li>
              </ul>
              <p className="mb-4 leading-relaxed">
                We are not responsible for the content of third-party advertisements or any products or services advertised. Clicking on advertisements may direct you to third-party websites governed by their own terms and privacy policies.
              </p>
            </section>

            {/* In-App Purchases and Payments */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                In-App Purchases and Payments
              </h2>
              <p className="mb-4 leading-relaxed">
                Our applications may offer in-app purchases, subscriptions, or other paid features. By making a purchase, you agree to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Pay all applicable fees and charges</li>
                <li>The payment terms of the app store or payment processor</li>
                <li>Automatic renewal of subscriptions unless cancelled before the renewal date</li>
              </ul>
              <p className="mb-4 leading-relaxed">
                All purchases are processed by third-party payment providers (Apple App Store, Paypal, Google Play Store, or other processors). Refund requests must be directed to the respective payment provider according to their refund policies. We do not have direct access to your payment information.
              </p>
            </section>

            {/* Subscription Terms */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Subscription Terms
              </h2>
              <p className="mb-4 leading-relaxed">
                If you purchase a subscription:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period</li>
                <li>Your account will be charged for renewal within 24 hours prior to the end of the current period</li>
                <li>You can manage and cancel subscriptions through your app store account settings</li>
                <li>Any unused portion of a free trial will be forfeited upon purchase of a subscription</li>
              </ul>
            </section>

            {/* Application Use */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Application Use
              </h2>
              <p className="mb-4 leading-relaxed">
                Our applications are provided for informational and utility purposes. You agree to use them responsibly and in compliance with all applicable laws and regulations.
              </p>
            </section>

            {/* Intellectual Property */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Intellectual Property
              </h2>
              <p className="mb-4 leading-relaxed">
                All content, features, and functionality of our applications, including but not limited to design, graphics, and code, are owned by us and are protected by intellectual property laws. Nothing in these terms grants you any right to use our trademarks, logos, or other proprietary information.
              </p>
            </section>

            {/* Third-Party Services */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Third-Party Services
              </h2>
              <p className="mb-4 leading-relaxed">
                Our applications integrate with third-party services including advertising networks and payment processors. These services are governed by their own terms and conditions. We are not responsible for the practices of these third parties and encourage you to review their terms before using their services.
              </p>
            </section>

            {/* Disclaimer of Warranties */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Disclaimer of Warranties
              </h2>
              <p className="mb-4 leading-relaxed">
                Our applications are provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the applications will be uninterrupted, error-free, or free of viruses or other harmful components.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Limitation of Liability
              </h2>
              <p className="mb-4 leading-relaxed">
                To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our applications, including but not limited to damages arising from advertisements, in-app purchases, or third-party services. Our total liability shall not exceed the amount you paid for the application or in-app purchases in the past twelve months.
              </p>
            </section>

            {/* Updates and Modifications */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Updates and Modifications
              </h2>
              <p className="mb-4 leading-relaxed">
                We reserve the right to modify, update, or discontinue our applications at any time without notice. We may also update these Terms of Service periodically. Continued use of the applications after any changes constitutes acceptance of the new terms.
              </p>
            </section>

            {/* Termination */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Termination
              </h2>
              <p className="mb-4 leading-relaxed">
                We may terminate or suspend your access to our applications at any time, without prior notice, for any reason including violation of these terms. Upon termination, your license to use the applications will immediately cease.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Governing Law
              </h2>
              <p className="mb-4 leading-relaxed">
                These Terms of Service shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-10">
              <h2 
                className="text-2xl font-semibold mb-4"
                style={{ color: currentTheme.primary, fontFamily: 'Playfair Display, serif' }}
              >
                Contact
              </h2>
              <p className="mb-4 leading-relaxed">
                If you have any questions about these Terms of Service, please contact us through the main application's support channels.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
